// BabyAI client — connects to Christopher's BabyAI Space at
// novasynchris-babyai.hf.space. OpenAI-compatible chat-completions
// shape, browser-direct, BYOK with a HuggingFace token.
//
// Three surfaces:
//   - callBabyAI         — single-model non-streaming
//   - callBabyAIStream   — single-model streaming (SSE)
//   - callBabyAIMoshPit  — 3 models in parallel, returns all candidates,
//                          non-streaming. The Playground's default — every
//                          team turn becomes routing-LoRA training signal
//                          when the user picks a tab.
//
// The Space accepts the OpenAI `tools` parameter but does not currently
// surface `tool_calls` in responses (verified empirically 2026-07-19).
// Playground v0 is pure chat — no tool surface — so that limit doesn't
// bite us yet.

import { useKeyStore } from "../store/keyStore";
import { readOpenAIStream } from "./openai-sse";

// Direct-to-HF endpoints (used when caller has a personal/channel HF token).
const BABYAI_ENDPOINT = "https://novasynchris-babyai.hf.space/v1/chat/completions";
const FEEDBACK_DIRECT_ENDPOINT = "https://novasynchris-babyai.hf.space/v1/feedback/select";

// Proxy endpoints (used when no token + `usePublic` is on). Same-origin —
// the Playground worker fronts the HF Space and attaches HF_TOKEN server-side.
const BABYAI_PROXY_ENDPOINT = "/api/babyai/v1/chat/completions";
const FEEDBACK_PROXY_ENDPOINT = "/api/babyai/v1/feedback/select";

/** Decide which endpoint + auth header to use for a BabyAI call.
 *  Precedence: explicit token > personal key > public proxy > error. */
function resolveCall(explicitToken?: string): { url: string; authHeader: string | null } | null {
  if (explicitToken) {
    return { url: BABYAI_ENDPOINT, authHeader: `Bearer ${explicitToken}` };
  }
  const { babyai: personalKey, usePublic } = useKeyStore.getState();
  if (personalKey) {
    return { url: BABYAI_ENDPOINT, authHeader: `Bearer ${personalKey}` };
  }
  if (usePublic) {
    return { url: BABYAI_PROXY_ENDPOINT, authHeader: null };
  }
  return null;
}

function resolveFeedback(): { url: string; authHeader: string | null } | null {
  const { babyai: personalKey, usePublic } = useKeyStore.getState();
  if (personalKey) {
    return { url: FEEDBACK_DIRECT_ENDPOINT, authHeader: `Bearer ${personalKey}` };
  }
  if (usePublic) {
    return { url: FEEDBACK_PROXY_ENDPOINT, authHeader: null };
  }
  return null;
}

const NO_AUTH_ERROR =
  "no BabyAI access — add a HuggingFace token in the Keys panel, " +
  "or turn on \"Use public BabyAI\" there for the free shared instance.";

export interface BabyAIMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface BabyAICallResult {
  text: string;
  model: string;
  usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
  stop_reason?: string;
}

export interface BabyAICallOptions {
  modelId: string;
  messages: BabyAIMessage[];
  systemInstruction?: string;
  maxTokens?: number;
  temperature?: number;
  /** Explicit token override — bypasses the personal-BYOK store.
   *  Used by chat-send to thread channel-owned keys through. */
  token?: string;
}

export async function callBabyAI(opts: BabyAICallOptions): Promise<BabyAICallResult> {
  const call = resolveCall(opts.token);
  if (!call) throw new Error(NO_AUTH_ERROR);

  const messages: BabyAIMessage[] = opts.systemInstruction
    ? [{ role: "system", content: opts.systemInstruction }, ...opts.messages]
    : opts.messages;

  const body = JSON.stringify({
    model: opts.modelId,
    messages,
    temperature: opts.temperature ?? 0.7,
    max_tokens: opts.maxTokens ?? 1024,
  });

  const resp = await postWithRetry(call.url, call.authHeader, body, false, "callBabyAI");

  if (!resp.ok) {
    const trimmed = await readErrorBody(resp);
    throw new Error(`BabyAI ${resp.status} ${resp.statusText}${trimmed ? ` — ${trimmed}` : ""}`);
  }

  type BabyAIResponse = {
    model?: string;
    choices?: Array<{ message?: { content?: string }; finish_reason?: string }>;
    usage?: BabyAICallResult["usage"];
  };
  let json: BabyAIResponse;
  try {
    json = (await resp.json()) as BabyAIResponse;
  } catch (e) {
    throw new Error(`BabyAI returned non-JSON: ${e instanceof Error ? e.message : String(e)}`);
  }

  return {
    text: json.choices?.[0]?.message?.content ?? "",
    model: json.model ?? opts.modelId,
    usage: json.usage,
    stop_reason: json.choices?.[0]?.finish_reason,
  };
}

export interface BabyAIStreamOptions extends BabyAICallOptions {
  onToken: (delta: string) => void;
}

export async function callBabyAIStream(opts: BabyAIStreamOptions): Promise<BabyAICallResult> {
  const call = resolveCall(opts.token);
  if (!call) throw new Error(NO_AUTH_ERROR);

  const messages: BabyAIMessage[] = opts.systemInstruction
    ? [{ role: "system", content: opts.systemInstruction }, ...opts.messages]
    : opts.messages;

  const body = JSON.stringify({
    model: opts.modelId,
    messages,
    temperature: opts.temperature ?? 0.7,
    max_tokens: opts.maxTokens ?? 1024,
    stream: true,
  });

  const resp = await postWithRetry(call.url, call.authHeader, body, true, "stream");
  if (!resp.ok) {
    const trimmed = await readErrorBody(resp);
    throw new Error(`BabyAI ${resp.status} ${resp.statusText}${trimmed ? ` — ${trimmed}` : ""}`);
  }

  const result = await readOpenAIStream(resp, opts.onToken);
  return {
    text: result.text,
    model: result.model ?? opts.modelId,
    usage: result.usage,
    stop_reason: result.finish_reason,
  };
}

// ---------------------------------------------------------------------------
// Mosh Pit — 3 models in parallel; the Playground default for team chat.
// ---------------------------------------------------------------------------
//
// `model: "auto"` triggers the Mosh Pit path server-side. mosh_pit_tier: 1
// pins to HF-only open models — keeps the routing-LoRA training signal pure
// and avoids spending paid-API budget on every chat turn.

export interface BabyAIAlternative {
  provider: string;
  providerName: string;
  modelId: string;
  tier: number;
  preferred: boolean;
  preferenceScore: number;
  tokens: number;
  content: string;
}

export interface BabyAIMoshPitResult {
  alternatives: BabyAIAlternative[];
  requestId: string;
  allProviders: string[];
}

export interface BabyAIMoshPitOptions {
  messages: BabyAIMessage[];
  systemInstruction?: string;
  maxTokens?: number;
  temperature?: number;
  /** Explicit token override — see BabyAICallOptions.token. */
  token?: string;
}

export async function callBabyAIMoshPit(opts: BabyAIMoshPitOptions): Promise<BabyAIMoshPitResult> {
  const call = resolveCall(opts.token);
  if (!call) throw new Error(NO_AUTH_ERROR);

  const messages: BabyAIMessage[] = opts.systemInstruction
    ? [{ role: "system", content: opts.systemInstruction }, ...opts.messages]
    : opts.messages;

  const body = JSON.stringify({
    model: "auto",
    messages,
    temperature: opts.temperature ?? 0.7,
    max_tokens: opts.maxTokens ?? 1024,
    mosh_pit_tier: 1,
  });

  const resp = await postWithRetry(call.url, call.authHeader, body, false, "mosh-pit");
  if (!resp.ok) {
    const trimmed = await readErrorBody(resp);
    throw new Error(`BabyAI ${resp.status} ${resp.statusText}${trimmed ? ` — ${trimmed}` : ""}`);
  }

  type MoshPitResponse = {
    id?: string;
    choices?: Array<{
      message?: { content?: string };
      babyai_choice?: {
        provider?: string;
        provider_name?: string;
        model_id?: string;
        tier?: number;
        preferred?: boolean;
        preference_score?: number;
        tokens?: number;
      };
    }>;
  };
  let json: MoshPitResponse;
  try {
    json = (await resp.json()) as MoshPitResponse;
  } catch (e) {
    throw new Error(`BabyAI returned non-JSON: ${e instanceof Error ? e.message : String(e)}`);
  }

  const choices = json.choices ?? [];
  if (choices.length === 0) throw new Error("BabyAI Mosh Pit returned no choices");

  const alternatives: BabyAIAlternative[] = choices
    .filter((c) => c.babyai_choice && c.message?.content)
    .map((c) => ({
      provider: c.babyai_choice!.provider ?? "",
      providerName: c.babyai_choice!.provider_name ?? c.babyai_choice!.provider ?? "?",
      modelId: c.babyai_choice!.model_id ?? "",
      tier: c.babyai_choice!.tier ?? 0,
      preferred: !!c.babyai_choice!.preferred,
      preferenceScore: c.babyai_choice!.preference_score ?? 0,
      tokens: c.babyai_choice!.tokens ?? 0,
      content: c.message!.content ?? "",
    }));

  if (alternatives.length === 0) {
    // Server didn't tag with babyai_choice — single-model fallback.
    // Render as a one-element alternative.
    const c = choices[0];
    alternatives.push({
      provider: "fallback",
      providerName: "BabyAI",
      modelId: "",
      tier: 0,
      preferred: true,
      preferenceScore: 1,
      tokens: 0,
      content: c.message?.content ?? "",
    });
  }

  alternatives.sort((a, b) => (a.preferred === b.preferred ? 0 : a.preferred ? -1 : 1));

  return {
    alternatives,
    requestId: json.id ?? "",
    allProviders: alternatives.map((a) => a.provider),
  };
}

// ---------------------------------------------------------------------------
// Selection feedback — tells BabyAI which alternative the user picked.
// Public-mode users feed signal through the proxy too — those Mosh Pit
// picks are the routing-LoRA training data and the whole point of
// giving public access away.
// ---------------------------------------------------------------------------

export interface SelectionFeedbackOptions {
  requestId: string;
  selectedProvider: string;
  allProviders: string[];
  taskType?: string;
}

/** Fire-and-forget. Telemetry should never break chat. */
export async function submitSelectionFeedback(opts: SelectionFeedbackOptions): Promise<void> {
  const fb = resolveFeedback();
  if (!fb) return;
  if (!opts.requestId || !opts.selectedProvider || opts.allProviders.length === 0) return;

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (fb.authHeader) headers.Authorization = fb.authHeader;

  try {
    const resp = await fetch(fb.url, {
      method: "POST",
      headers,
      body: JSON.stringify({
        request_id: opts.requestId,
        selected_provider: opts.selectedProvider,
        all_providers: opts.allProviders,
        task_type: opts.taskType,
      }),
    });
    if (!resp.ok) {
      console.warn(`[babyai] feedback/select returned ${resp.status} ${resp.statusText}`);
    }
  } catch (e) {
    console.warn(`[babyai] feedback/select failed:`, e);
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readErrorBody(resp: Response): Promise<string> {
  try {
    const body = await resp.text();
    return body.length > 240 ? body.slice(0, 240) + "…" : body;
  } catch {
    return "";
  }
}

/** Shared POST with one-shot 5xx + network retry. Used by every BabyAI
 *  surface (single, stream, mosh-pit). `tag` is used only in console
 *  warnings to disambiguate which surface logged the retry. */
async function postWithRetry(
  url: string,
  authHeader: string | null,
  body: string,
  stream: boolean,
  tag: string,
): Promise<Response> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authHeader) headers.Authorization = authHeader;
  if (stream) headers.Accept = "text/event-stream";

  let resp: Response | null = null;
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      resp = await fetch(url, { method: "POST", headers, body });
    } catch (e) {
      lastError = new Error(
        `BabyAI fetch failed (network or CORS): ${e instanceof Error ? e.message : String(e)}`,
      );
      if (attempt === 0) {
        console.warn(`[babyai] network error (${tag}), retrying once in 1s:`, e);
        await sleep(1000);
        continue;
      }
      throw lastError;
    }
    if (resp.status >= 500 && resp.status < 600 && attempt === 0) {
      console.warn(`[babyai] HTTP ${resp.status} (${tag}), retrying once in 1s`);
      await sleep(1000);
      continue;
    }
    break;
  }
  if (!resp) throw lastError ?? new Error("BabyAI: no response after retry");
  return resp;
}
